/**
 * @file useMenuLinkNavigation.js
 * @description Custom hook that attaches a click interceptor to a TipTap editor
 * DOM node, handling navigation to menu routes (/my-assets, /my-benefits,
 * /my-messages, home). In preview or view-live mode it shows an info toast instead.
 * In live mode it applies per-route access checks and lands the user on the
 * Individual side before navigating.
 */
import { useEffect, useRef } from 'react';
import { ROUTES } from '../constants/routes';
import { useAuthContext } from '../store/Contexts';
import { useIndividualHasBenefitAccess } from './useIndividualHasBenefitAccess';
import { useIndividualViewNavigation } from './useIndividualViewNavigation';
import { UserType, SecurityAttributes } from '../utils/data/enums';
import Toast from '../components/ui/toasts/Toast';
import { getText } from '../i18n';

const MENU_ROUTES = [
    ROUTES.MY_ASSETS,
    ROUTES.MY_BENEFITS,
    ROUTES.MY_MESSAGES,
    ROUTES.ROOT,
    ROUTES.MY_HOME,
];

// Menu links are stored relative in portal content and absolute in content sent outside the app.
function resolveMenuRoute(anchor) {
    const href = anchor.getAttribute('href');
    if (!href) {
        return null;
    }

    try {
        const parsedUrl = new URL(href, window.location.origin);
        if (parsedUrl.origin !== window.location.origin) {
            return null;
        }

        if (!MENU_ROUTES.includes(parsedUrl.pathname)) {
            return null;
        }

        // /my-home only exists so external links can target the Individual home page.
        return parsedUrl.pathname === ROUTES.MY_HOME
            ? ROUTES.ROOT
            : parsedUrl.pathname;
    } catch (error) {
        return null;
    }
}

function useMenuLinkNavigation(
    editor,
    { isPreview = false, isViewLive = false } = {},
) {
    const { navigateToIndividualRoute } = useIndividualViewNavigation();
    const { user } = useAuthContext();
    const showIndividualBenefits = useIndividualHasBenefitAccess();
    const isLimitedUser = user?.jwt?.type === UserType.LimitedUser;

    const { hasSecurityAttribute } = useAuthContext();

    // The click listener outlives the render that registered it, so read the latest
    // navigation callback (and the admin view it depends on) through a ref.
    const navigateToIndividualRouteRef = useRef(navigateToIndividualRoute);
    navigateToIndividualRouteRef.current = navigateToIndividualRoute;

    useEffect(() => {
        if (!editor) return undefined;

        let dom;
        try {
            dom = editor.view?.dom;
        } catch (error) {
            return undefined;
        }

        if (!dom || typeof dom.addEventListener !== 'function') {
            return undefined;
        }

        function handleMenuLinkClick(e) {
            const anchor = e.target.closest('a');
            if (!anchor) return;
            const href = resolveMenuRoute(anchor);
            if (!href) return;
            e.preventDefault();
            e.stopPropagation();
            if (isPreview || isViewLive) {
                Toast.info(getText('msg_7ebb77bbfd9c'));
                return;
            }
            if (href === ROUTES.MY_MESSAGES) {
                if (isLimitedUser) {
                    Toast.error(getText('msg_4cf125274798'));
                    return;
                }
                navigateToIndividualRouteRef.current(href);
            } else if (href === ROUTES.MY_BENEFITS) {
                if (
                    !showIndividualBenefits ||
                    (isLimitedUser &&
                        !hasSecurityAttribute([
                            SecurityAttributes.UserAccessBenefits,
                        ]))
                ) {
                    Toast.error(getText('msg_b635a89257ec'));
                    return;
                }
                navigateToIndividualRouteRef.current(href);
            } else if (href === ROUTES.MY_ASSETS) {
                navigateToIndividualRouteRef.current(href);
            } else if (href === ROUTES.ROOT) {
                navigateToIndividualRouteRef.current(href);
            }
        }

        dom.addEventListener('click', handleMenuLinkClick, true);
        return () => {
            if (typeof dom.removeEventListener === 'function') {
                dom.removeEventListener('click', handleMenuLinkClick, true);
            }
        };
    }, [
        editor,
        isPreview,
        isViewLive,
        isLimitedUser,
        showIndividualBenefits,
        hasSecurityAttribute,
    ]);
}

export { useMenuLinkNavigation };
